/**
 * The replies a person has sent that the thread doesn't hold yet (contracts
 * §9.1's client-made `id`): shown at once, "Sending…", then the real message
 * and turn; or "Not sent" with Retry (the same id again, so it's never sent
 * twice) and Edit. They live here, by thread, not only in the query cache, so
 * a refetch of the thread never wipes one; `withUnsent` merges them into what
 * a view renders, and `settle` drops each once a loaded thread holds its id.
 *
 * A new thread's first message is one too, marked `start` (`starts.ts`): it
 * goes with `POST /api/threads` and its client-made pair, and the thread's
 * replies wait behind it.
 */
import { useCallback, useSyncExternalStore } from "react";
import { ApiError, type Uploaded } from "../../app/api";
import type { ThreadDetail, ThreadMessage, Turn } from "./api";
import type { ViewMessage } from "./projection";

export type UnsentState = "sending" | "failed" | "sent";

export type Unsent = {
  /** The message's id, made here (`crypto.randomUUID()`) and sent with it. */
  id: string;
  thread_id: string;
  author_user_id: string;
  body: string;
  doc_id: string | null;
  /** Slice 5b (spec §6.2): files uploaded with it, sent by id; Retry sends them again. */
  files?: Uploaded[];
  created_at: string;
  state: UnsentState;
  /** Why it wasn't sent, for its line. */
  error?: unknown;
  /** Once sent: what the API returned, shown until a loaded thread holds it. */
  sent?: { message: ThreadMessage; turn: Turn };
  /** The thread's first message, which starts it (`POST /api/threads`, its id the thread's). */
  start?: true;
  /** A start on an Area's page (slice 6d spec §4.2): the chat is filed there. */
  area_id?: string | null;
};

/** A view message still in the outbox: "sending" or "failed" (a sent one shows as it is). */
export type UnsentView = ViewMessage & { unsent?: Exclude<UnsentState, "sent"> };

/** The local turn standing in for a reply being sent: its line reads "Sending…". */
const LOCAL_TURN = "local:";
export const isLocalTurn = (turn: Pick<Turn, "id">) => turn.id.startsWith(LOCAL_TURN);

export function newUnsent(
  threadId: string,
  authorId: string,
  ask: { body: string; doc_id?: string | null; files?: Uploaded[] },
  now = new Date(),
  /** An Edited reply's id, sent again with the same words (`resendMatch`). */
  id: string = crypto.randomUUID(),
): Unsent {
  return {
    id,
    thread_id: threadId,
    author_user_id: authorId,
    body: ask.body,
    doc_id: ask.doc_id ?? null,
    ...(ask.files?.length ? { files: ask.files } : {}),
    created_at: now.toISOString(),
    state: "sending",
  };
}

/** A new thread's first message: sent with `thread_id` + `message_id` (contracts §9.1). */
export function newStart(
  threadId: string,
  authorId: string,
  ask: { body: string; doc_id?: string | null; area_id?: string | null; files?: Uploaded[] },
  now = new Date(),
  id?: string,
): Unsent {
  const start: Unsent = { ...newUnsent(threadId, authorId, ask, now, id), start: true };
  return ask.area_id ? { ...start, area_id: ask.area_id } : start;
}

/** A thread's start, while this tab holds it. */
export const startOf = (list: Unsent[]) => list.find((u) => u.start);

/** The start this thread is still waiting on (sending or Not sent): it doesn't exist yet. */
export function pendingStart(list: Unsent[]): Unsent | undefined {
  const start = startOf(list);
  return start && start.state !== "sent" ? start : undefined;
}

/**
 * Why a reply behind a start that didn't land wasn't sent: never sent, so
 * refused (it made nothing). Retrying the start sends it after.
 */
export const notStarted = () =>
  new ApiError(0, "not_started", "Its thread didn't start. Retry the first message.");

/**
 * The Worker answered and refused it (a 4xx: `ai_not_connected`, `invalid`):
 * it certainly made nothing. Anything else (no answer, a timeout, a 5xx)
 * may have landed.
 */
export const refused = (error: unknown) => error instanceof ApiError && error.status < 500;

/** A reply taken back by Edit whose send may have landed: its id and words. */
export type Reclaimed = { id: string; body: string };

/**
 * The id a send goes with after an Edit (Devin on #281): an Edited reply's
 * own when its words are sent again, so a send that had landed isn't made
 * twice (contracts §9.1's repeat). Only a match is used up; every other
 * reclaimed reply keeps its id, whatever was sent in between.
 */
export function resendMatch<R extends Reclaimed>(
  reclaimed: R[],
  body: string,
): { id: string | null; rest: R[] } {
  // Two Edited replies with the same words: the latest Edit's, whose words the composer got last.
  const match = [...reclaimed].reverse().find((r) => r.body === body);
  return match
    ? { id: match.id, rest: reclaimed.filter((r) => r !== match) }
    : { id: null, rest: reclaimed };
}

const patch = (list: Unsent[], id: string, change: Partial<Unsent>) =>
  list.map((u) => (u.id === id ? { ...u, ...change } : u));

/** The outbox's moves, pure. Unknown ids change nothing. */
export const outboxMoves = {
  add: (list: Unsent[], u: Unsent) => (list.some((x) => x.id === u.id) ? list : [...list, u]),
  sending: (list: Unsent[], id: string) => patch(list, id, { state: "sending", error: undefined }),
  failed: (list: Unsent[], id: string, error: unknown) =>
    patch(list, id, { state: "failed", error }),
  sent: (list: Unsent[], id: string, sent: Unsent["sent"]) =>
    patch(list, id, { state: "sent", error: undefined, sent }),
  drop: (list: Unsent[], id: string) => list.filter((u) => u.id !== id),
  /**
   * A loaded thread holds these ids: they're sent, whatever this tab thought
   * (a send that timed out may have landed). The same array when none go.
   */
  settle: (list: Unsent[], loaded: Set<string>) =>
    list.some((u) => loaded.has(u.id)) ? list.filter((u) => !loaded.has(u.id)) : list,
};

/** An unsent message's files as the thread lists them, so their chips show at once. */
function attachedOf(files: Uploaded[] | undefined) {
  if (!files?.length) return {};
  const ref = (f: Uploaded) => ({ id: f.id, name: f.name, mime: f.mime, size: f.size });
  const photos = files.filter((f) => f.kind === "photo").map(ref);
  const rest = files.filter((f) => f.kind !== "photo").map(ref);
  return { ...(photos.length ? { photos } : {}), ...(rest.length ? { files: rest } : {}) };
}

function localMessage(u: Unsent): ThreadMessage {
  return {
    id: u.id,
    thread_id: u.thread_id,
    author_user_id: u.author_user_id,
    author_agent_id: null,
    kind: "text",
    body: u.body,
    data: { ...(u.doc_id ? { doc_id: u.doc_id } : {}), ...attachedOf(u.files) },
    turn_id: null,
    part: null,
    stream_seq: null,
    created_at: u.created_at,
  };
}

function localTurn(u: Unsent): Turn {
  return {
    id: `${LOCAL_TURN}${u.id}`,
    thread_id: u.thread_id,
    agent_id: "",
    status: "queued",
    fail_reason: null,
    progress: null,
    trigger_message_id: u.id,
    deferred_at: null,
    created_at: u.created_at,
    started_at: null,
    finished_at: null,
    can_retry: false,
  };
}

/**
 * What a view renders with the outbox merged in: each unsent reply the
 * messages don't hold yet after them, in the order it was sent, keyed by its
 * id (the real message's key too, so nothing flickers when it lands). A
 * sending one gets a local turn ("Sending…"); a sent one its real turn,
 * until the thread holds it.
 */
export function withUnsent(
  turns: Turn[],
  messages: ViewMessage[],
  unsent: Unsent[],
): { turns: Turn[]; messages: UnsentView[] } {
  if (unsent.length === 0) return { turns, messages };
  const have = new Set(messages.map((m) => m.id));
  const haveTurns = new Set(turns.map((t) => t.id));
  const extraMessages: UnsentView[] = [];
  const extraTurns: Turn[] = [];
  for (const u of unsent) {
    if (have.has(u.id)) continue;
    const message = u.sent?.message ?? localMessage(u);
    extraMessages.push({
      ...message,
      key: message.id,
      ...(u.state === "sent" ? {} : { unsent: u.state }),
    });
    if (u.state === "sending") extraTurns.push(localTurn(u));
    if (u.sent && !haveTurns.has(u.sent.turn.id)) extraTurns.push(u.sent.turn);
  }
  return { turns: [...extraTurns, ...turns], messages: [...messages, ...extraMessages] };
}

/** The merged thread a view lays out: `detail` with the outbox's turns, and the messages. */
export function unsentLayout(detail: ThreadDetail, messages: ViewMessage[], unsent: Unsent[]) {
  const merged = withUnsent(detail.turns, messages, unsent);
  return { detail: { ...detail, turns: merged.turns }, messages: merged.messages };
}

// The store: one list per thread, for every view of it (Chats and the side panel).

const NONE: Unsent[] = [];
const lists = new Map<string, Unsent[]>();
const listeners = new Map<string, Set<() => void>>();
const reclaimed = new Map<string, Reclaimed[]>();
/** Starts taken back by Edit that may have landed: the same words start the same thread. */
let reclaimedStarts: Array<Reclaimed & { thread_id: string }> = [];
/** Every thread's start, for the lists (`starts.ts`), and who's watching them. */
let starts: Unsent[] = NONE;
const startListeners = new Set<() => void>();

function changed(threadId: string) {
  const next = [...lists.values()].flatMap((l) => l.filter((u) => u.start));
  const same = next.length === starts.length && next.every((u, i) => u === starts[i]);
  if (!same) {
    starts = next.length > 0 ? next : NONE;
    for (const listener of startListeners) listener();
  }
  for (const listener of listeners.get(threadId) ?? []) listener();
}

export const outbox = {
  /** Edit took back a reply that may have landed: a send of its words reuses its id. */
  reclaim(threadId: string, r: Reclaimed) {
    const others = (reclaimed.get(threadId) ?? []).filter((x) => x.id !== r.id);
    reclaimed.set(threadId, [...others, r]);
  },
  /** The id of a reclaimed reply with these words, used up; null for a new one. */
  resendId(threadId: string, body: string): string | null {
    const { id, rest } = resendMatch(reclaimed.get(threadId) ?? [], body);
    if (rest.length > 0) reclaimed.set(threadId, rest);
    else reclaimed.delete(threadId);
    return id;
  },
  /**
   * A loaded thread holds these ids: the outbox lets them go, and a reclaimed
   * one landed and shows, so sending its words now is a new reply.
   */
  settle(threadId: string, loaded: Set<string>) {
    const left = (reclaimed.get(threadId) ?? []).filter((r) => !loaded.has(r.id));
    if (left.length > 0) reclaimed.set(threadId, left);
    else reclaimed.delete(threadId);
    // A reclaimed start shown landed too: its words start a new thread now.
    reclaimedStarts = reclaimedStarts.filter((r) => !loaded.has(r.id));
    outbox.update(threadId, (l) => outboxMoves.settle(l, loaded));
  },
  /** Edit took back a start that may have landed: its words start the same thread again. */
  reclaimStart(r: Reclaimed & { thread_id: string }) {
    reclaimedStarts = [...reclaimedStarts.filter((x) => x.id !== r.id), r];
  },
  /** The pair a start with these words goes with after an Edit, used up; null for a new one. */
  restartIds(body: string): { thread_id: string; message_id: string } | null {
    const { id, rest } = resendMatch(reclaimedStarts, body);
    const match = reclaimedStarts.find((r) => r.id === id);
    reclaimedStarts = rest;
    return match ? { thread_id: match.thread_id, message_id: match.id } : null;
  },
  /** Logging out: nothing this person was sending stays for the next one. */
  clear() {
    const threads = [...lists.keys()];
    lists.clear();
    reclaimed.clear();
    reclaimedStarts = [];
    for (const id of threads) changed(id);
  },
  get: (threadId: string): Unsent[] => lists.get(threadId) ?? NONE,
  find: (threadId: string, id: string) => outbox.get(threadId).find((u) => u.id === id),
  update(threadId: string, move: (list: Unsent[]) => Unsent[]) {
    const prev = outbox.get(threadId);
    const next = move(prev);
    if (next === prev) return;
    if (next.length === 0) lists.delete(threadId);
    else lists.set(threadId, next);
    changed(threadId);
  },
  /** Every thread's start this tab holds, sending, Not sent or sent. */
  starts: () => starts,
  subscribeStarts: (listener: () => void) => {
    startListeners.add(listener);
    return () => void startListeners.delete(listener);
  },
  subscribe(threadId: string, listener: () => void) {
    const set = listeners.get(threadId) ?? new Set();
    set.add(listener);
    listeners.set(threadId, set);
    return () => {
      set.delete(listener);
      if (set.size === 0) listeners.delete(threadId);
    };
  },
};

/** A thread's unsent replies, live. */
export function useUnsent(threadId: string): Unsent[] {
  const subscribe = useCallback(
    (listener: () => void) => outbox.subscribe(threadId, listener),
    [threadId],
  );
  const snapshot = useCallback(() => outbox.get(threadId), [threadId]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Every start this tab holds, live: the thread lists show them before they load. */
export function useStarts(): Unsent[] {
  return useSyncExternalStore(outbox.subscribeStarts, outbox.starts, outbox.starts);
}
